import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button, Spinner } from '@nextui-org/react';
import {
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Compass,
  FilterX,
  LocateFixed,
  MapPin,
  MapPinOff,
  Navigation,
  RotateCcw,
  ShoppingBag,
  Trophy,
  Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AddressSearchInput from '../components/AddressSearchInput';
import { GeocodeResult } from '../services/userService';
import appDataService, { Club, Pub, Shop, Series } from '../services/appDataService';
import tournamentService, { Tournament } from '../services/tournamentService';

// ---------------------------------------------------------------------------
// Typen (alle Felder nullable für maximale Robustheit)
// ---------------------------------------------------------------------------

type NearbyType = 'tournament' | 'club' | 'pub' | 'shop' | 'series';

interface NearbyEntry {
  id?: string | null;
  name?: string | null;
  type: NearbyType;
  slug?: string | null;
  image?: string | null;
  subtitle?: string | null;
  distance: number;
}

interface GeoPoint {
  latitude: number;
  longitude: number;
  label?: string | null;
}

interface SectionConfig {
  key: NearbyType;
  title: string;
  badgeLabel: string;
  routePrefix: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  subtitleIcon: React.ComponentType<{ size?: number | string; className?: string }>;
}

const SECTIONS: SectionConfig[] = [
  { key: 'tournament', title: 'Turniere', badgeLabel: 'Turnier', routePrefix: '/turnier', icon: Trophy, subtitleIcon: CalendarDays },
  { key: 'club', title: 'Vereine', badgeLabel: 'Verein', routePrefix: '/verein', icon: Users, subtitleIcon: MapPin },
  { key: 'pub', title: 'Spielstätten', badgeLabel: 'Spielstätte', routePrefix: '/spielstaette', icon: MapPin, subtitleIcon: MapPin },
  { key: 'shop', title: 'Shops', badgeLabel: 'Shop', routePrefix: '/shop', icon: ShoppingBag, subtitleIcon: MapPin },
  { key: 'series', title: 'Serien', badgeLabel: 'Serie', routePrefix: '/turnierserie', icon: CalendarRange, subtitleIcon: MapPin },
];

const DISTANCE_OPTIONS = [10, 25, 50, 100, 200, 500];

// ---------------------------------------------------------------------------
// Helper (defensiv, wie Flutter NearbyView)
// ---------------------------------------------------------------------------

const toFiniteNumber = (value: any): number | null => {
  if (typeof value === 'number' && isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (isFinite(parsed)) return parsed;
  }
  return null;
};

/** Distanz-Format wie Flutter: "850 m", "12,3 km", "42 km" */
const formatDistance = (km: number): string => {
  if (!isFinite(km) || km < 0) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
};

/** Turnier-Datum wie Flutter: "5.7.2026" bzw. "5.7. - 6.7.2026" */
const formatTournamentDates = (dates?: number[] | null): string => {
  if (!dates || !Array.isArray(dates)) return '';
  const valid = dates
    .filter((d) => typeof d === 'number' && isFinite(d))
    .slice()
    .sort((a, b) => a - b);
  if (valid.length === 0) return '';
  const first = new Date(valid[0]);
  if (isNaN(first.getTime())) return '';
  if (valid.length === 1) {
    return `${first.getDate()}.${first.getMonth() + 1}.${first.getFullYear()}`;
  }
  const last = new Date(valid[valid.length - 1]);
  if (isNaN(last.getTime())) {
    return `${first.getDate()}.${first.getMonth() + 1}.${first.getFullYear()}`;
  }
  return `${first.getDate()}.${first.getMonth() + 1}. - ${last.getDate()}.${last.getMonth() + 1}.${last.getFullYear()}`;
};

/** Thumbnail für Club/Pub/Shop/Series (Priorität: logo > image > images[0]) */
const entityThumbnail = (item?: Club | Pub | Shop | Series | null): string | null => {
  if (!item || typeof item !== 'object') return null;
  const anyItem = item as any;
  let file: string | null = null;
  if (typeof anyItem.logo === 'string' && anyItem.logo.trim()) {
    file = anyItem.logo.trim();
  } else if (typeof item.image === 'string' && item.image.trim()) {
    file = item.image.trim();
  } else if (Array.isArray(item.images) && typeof item.images[0] === 'string' && item.images[0].trim()) {
    file = item.images[0].trim();
  }
  return tournamentService.buildThumbnailUrl(file, 200);
};

// ---------------------------------------------------------------------------
// Kleine UI-Bausteine
// ---------------------------------------------------------------------------

const FilterChip: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap ${
      selected
        ? 'border-[#3fced1] bg-[#3fced1]/15 text-[#3fced1] font-semibold'
        : 'border-zinc-700 text-gray-400 hover:border-zinc-500 hover:text-gray-300'
    }`}
  >
    {label}
  </button>
);

/** Karten-Bild 100x100 mit Icon-Fallback (auch bei Ladefehler) */
const EntryImage: React.FC<{
  src?: string | null;
  alt: string;
  fallbackIcon: React.ComponentType<{ size?: number | string; className?: string }>;
}> = ({ src, alt, fallbackIcon: FallbackIcon }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative w-[100px] h-[100px] shrink-0 rounded-xl overflow-hidden bg-zinc-800 flex items-center justify-center">
      <FallbackIcon size={36} className="text-zinc-600" />
      {src && !failed && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hauptseite
// ---------------------------------------------------------------------------

const NearbyPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();

  // Standort
  const [customLocation, setCustomLocation] = useState<GeoPoint | null>(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  // Daten
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [rawTournaments, setRawTournaments] = useState<Tournament[]>([]);
  const [rawClubs, setRawClubs] = useState<Club[]>([]);
  const [rawPubs, setRawPubs] = useState<Pub[]>([]);
  const [rawShops, setRawShops] = useState<Shop[]>([]);
  const [rawSeries, setRawSeries] = useState<Series[]>([]);

  // Filter
  const [maxDistance, setMaxDistance] = useState<number>(100);
  const [typeFilters, setTypeFilters] = useState<Record<NearbyType, boolean>>({
    tournament: true,
    club: true,
    pub: true,
    shop: true,
    series: true,
  });
  const [collapsedSections, setCollapsedSections] = useState<Set<NearbyType>>(new Set());

  // Profil-Standort aus dem eingeloggten User (defensiv geparst)
  const profileLocation = useMemo<GeoPoint | null>(() => {
    const address = (user as any)?.personalData?.address;
    if (!address || typeof address !== 'object') return null;
    const lat = toFiniteNumber(address.latitude);
    const lng = toFiniteNumber(address.longitude);
    if (lat === null || lng === null) return null;
    let label: string | null = null;
    if (typeof address.label === 'string' && address.label.trim()) {
      label = address.label.trim();
    } else {
      const parts = [address.street, address.city, address.country]
        .filter((p: any) => typeof p === 'string' && p.trim())
        .map((p: string) => p.trim());
      label = parts.length > 0 ? parts.join(', ') : 'Profil-Adresse';
    }
    return { latitude: lat, longitude: lng, label };
  }, [user]);

  const activeLocation = customLocation ?? profileLocation;

  // Alle 5 Entity-Typen parallel laden, sobald ein Standort vorhanden ist
  useEffect(() => {
    if (!activeLocation || dataLoaded || dataLoading) return;
    let cancelled = false;

    const load = async () => {
      setDataLoading(true);
      try {
        const [tournamentRes, clubsData, pubsData, shopsData, seriesData] = await Promise.all([
          tournamentService.getTournaments().catch(() => null),
          appDataService.getClubs().catch(() => [] as Club[]),
          appDataService.getPubs().catch(() => [] as Pub[]),
          appDataService.getShops().catch(() => [] as Shop[]),
          appDataService.getSeries().catch(() => [] as Series[]),
        ]);
        if (cancelled) return;
        // Nur valide UND kommende Turniere (wie Flutter getValidTournaments)
        setRawTournaments(
          tournamentService.filterValidTournaments(tournamentRes?.data ?? [], true)
        );
        setRawClubs(Array.isArray(clubsData) ? clubsData : []);
        setRawPubs(Array.isArray(pubsData) ? pubsData : []);
        setRawShops(Array.isArray(shopsData) ? shopsData : []);
        setRawSeries(Array.isArray(seriesData) ? seriesData : []);
        setDataLoaded(true);
      } catch (error) {
        console.error('Fehler beim Laden der Umgebungs-Daten:', error);
        if (!cancelled) setDataLoaded(true);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [activeLocation, dataLoaded, dataLoading]);

  // Distanzen berechnen + sortieren (Haversine, Erdradius 6371 km)
  const entriesByType = useMemo<Record<NearbyType, NearbyEntry[]>>(() => {
    const result: Record<NearbyType, NearbyEntry[]> = {
      tournament: [],
      club: [],
      pub: [],
      shop: [],
      series: [],
    };
    if (!activeLocation) return result;
    const { latitude: userLat, longitude: userLng } = activeLocation;

    for (const t of rawTournaments) {
      const lat = toFiniteNumber(t?.address?.latitude);
      const lng = toFiniteNumber(t?.address?.longitude);
      if (lat === null || lng === null) continue;
      result.tournament.push({
        id: t._id ?? t.id ?? null,
        name: t.name ?? null,
        type: 'tournament',
        slug: t.slug ?? null,
        image: tournamentService.getThumbnailUrl(t, 200),
        subtitle: formatTournamentDates(t.dates),
        distance: tournamentService.calculateDistance(userLat, userLng, lat, lng),
      });
    }

    const processEntity = (
      items: (Club | Pub | Shop | Series)[],
      type: NearbyType
    ) => {
      for (const item of items) {
        if (!item || typeof item !== 'object') continue;
        const lat = toFiniteNumber(item.address?.latitude);
        const lng = toFiniteNumber(item.address?.longitude);
        if (lat === null || lng === null) continue;
        result[type].push({
          id: item._id ?? item.id ?? null,
          name: item.name ?? null,
          type,
          slug: item.slug ?? null,
          image: entityThumbnail(item),
          subtitle:
            typeof item.address?.label === 'string' && item.address.label.trim()
              ? item.address.label.trim()
              : appDataService.formatAddress(item.address),
          distance: tournamentService.calculateDistance(userLat, userLng, lat, lng),
        });
      }
    };

    processEntity(rawClubs, 'club');
    processEntity(rawPubs, 'pub');
    processEntity(rawShops, 'shop');
    processEntity(rawSeries, 'series');

    (Object.keys(result) as NearbyType[]).forEach((key) => {
      result[key].sort((a, b) => a.distance - b.distance);
    });
    return result;
  }, [activeLocation, rawTournaments, rawClubs, rawPubs, rawShops, rawSeries]);

  // Radius-Filter anwenden
  const filteredByType = useMemo<Record<NearbyType, NearbyEntry[]>>(() => {
    const result: Record<NearbyType, NearbyEntry[]> = {
      tournament: [],
      club: [],
      pub: [],
      shop: [],
      series: [],
    };
    (Object.keys(entriesByType) as NearbyType[]).forEach((key) => {
      result[key] = entriesByType[key].filter((e) => e.distance <= maxDistance);
    });
    return result;
  }, [entriesByType, maxDistance]);

  // -------------------------------------------------------------------------
  // Standort-Aktionen
  // -------------------------------------------------------------------------

  const handleAddressSelect = (geocode: GeocodeResult) => {
    setGeoError(null);
    const lat = toFiniteNumber(geocode?.latitude);
    const lng = toFiniteNumber(geocode?.longitude);
    if (lat === null || lng === null) {
      setGeoError('Für diese Adresse wurden keine Koordinaten gefunden.');
      return;
    }
    setCustomLocation({
      latitude: lat,
      longitude: lng,
      label: typeof geocode?.formattedAddress === 'string' && geocode.formattedAddress.trim()
        ? geocode.formattedAddress.trim()
        : 'Gewählter Standort',
    });
  };

  const useBrowserLocation = () => {
    setGeoError(null);
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGeoError('Standortbestimmung wird von deinem Browser nicht unterstützt.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGeoLoading(false);
        const lat = toFiniteNumber(position?.coords?.latitude);
        const lng = toFiniteNumber(position?.coords?.longitude);
        if (lat === null || lng === null) {
          setGeoError('Dein Standort konnte nicht ermittelt werden.');
          return;
        }
        setCustomLocation({ latitude: lat, longitude: lng, label: 'Mein aktueller Standort' });
      },
      (error) => {
        setGeoLoading(false);
        if (error?.code === 1) {
          setGeoError('Standortfreigabe wurde abgelehnt. Bitte erlaube den Zugriff in deinen Browser-Einstellungen.');
        } else if (error?.code === 3) {
          setGeoError('Zeitüberschreitung bei der Standortbestimmung. Bitte versuche es erneut.');
        } else {
          setGeoError('Dein Standort konnte nicht ermittelt werden.');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
    );
  };

  const resetToProfileLocation = () => {
    setCustomLocation(null);
    setAddressQuery('');
    setGeoError(null);
  };

  const toggleTypeFilter = (type: NearbyType) => {
    setTypeFilters((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleSection = (type: NearbyType) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // -------------------------------------------------------------------------
  // Abgeleitete Render-Zustände
  // -------------------------------------------------------------------------

  const isLoading = authLoading || (Boolean(activeLocation) && (dataLoading || !dataLoaded));
  const noFiltersSelected = !typeFilters.tournament && !typeFilters.club && !typeFilters.pub && !typeFilters.shop && !typeFilters.series;
  const nothingToShow = SECTIONS.every(
    (section) => !typeFilters[section.key] || filteredByType[section.key].length === 0
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <Helmet>
        <title>In der Nähe - Turniere, Vereine & Spielstätten in deiner Umgebung | DartCloud</title>
        <meta
          name="description"
          content="Finde Dart-Turniere, Vereine, Spielstätten, Shops und Turnierserien in deiner Umgebung. Standort wählen, Umkreis festlegen und losspielen."
        />
        <meta
          name="keywords"
          content="dart in der nähe, dart turniere umgebung, dartverein finden, dart spielstätte nähe, dart shop nähe"
        />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8 flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-[#3fced1]/10 shrink-0">
              <Compass size={32} className="text-[#3fced1]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-1 text-white">In der Nähe</h1>
              <p className="text-gray-400 text-sm md:text-base">
                Finde Turniere, Vereine und mehr in deiner Umgebung
              </p>
            </div>
          </div>

          {/* Standort-Karte */}
          <div className="mb-6 rounded-xl border border-zinc-800 bg-zinc-900 p-4">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <LocateFixed size={18} className="text-gray-400 shrink-0" />
              <span className="text-sm text-gray-400">Aktueller Standort:</span>
              <span className="text-sm font-semibold text-white truncate max-w-full">
                {activeLocation?.label || (activeLocation ? 'Standort gesetzt' : 'Nicht festgelegt')}
              </span>
              {customLocation && (
                <Button
                  size="sm"
                  variant="light"
                  className="text-gray-300 h-7 min-w-0 px-2"
                  startContent={<RotateCcw size={14} />}
                  onPress={resetToProfileLocation}
                >
                  Zurücksetzen
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
              <AddressSearchInput
                value={addressQuery}
                onValueChange={setAddressQuery}
                onSelect={handleAddressSelect}
                label="Anderen Standort suchen"
                placeholder="z.B. Hamburg, München, Berlin..."
              />
              <Button
                className="bg-[#3fced1] text-black font-semibold md:mt-1 w-full md:w-auto"
                startContent={!geoLoading ? <Navigation size={16} /> : undefined}
                isLoading={geoLoading}
                onPress={useBrowserLocation}
              >
                Meinen Standort verwenden
              </Button>
            </div>

            {geoError && (
              <p className="mt-3 text-sm text-red-400">{geoError}</p>
            )}
          </div>

          {/* Filter-Bar */}
          <div className="mb-8 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-300 mr-1">Umkreis:</span>
              {DISTANCE_OPTIONS.map((distance) => (
                <FilterChip
                  key={distance}
                  label={`${distance} km`}
                  selected={maxDistance === distance}
                  onClick={() => setMaxDistance(distance)}
                />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-300 mr-1">Anzeigen:</span>
              {SECTIONS.map((section) => (
                <FilterChip
                  key={section.key}
                  label={section.title}
                  selected={typeFilters[section.key]}
                  onClick={() => toggleTypeFilter(section.key)}
                />
              ))}
            </div>
          </div>

          {/* Inhalt */}
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="lg" color="primary" />
            </div>
          ) : !activeLocation ? (
            /* Kein Standort gesetzt */
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-10 text-center">
              <MapPinOff size={56} className="mx-auto text-zinc-600 mb-4" />
              <h2 className="text-lg font-semibold text-gray-300 mb-2">Kein Standort festgelegt</h2>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Suche oben nach einer Adresse, nutze den Button „Meinen Standort verwenden“ oder
                hinterlege eine Adresse in deinem Profil, um Turniere, Vereine, Spielstätten,
                Shops und Serien in deiner Umgebung zu finden.
              </p>
            </div>
          ) : noFiltersSelected ? (
            /* Alle Typ-Filter deaktiviert */
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-10 text-center">
              <FilterX size={56} className="mx-auto text-zinc-600 mb-4" />
              <h2 className="text-lg font-semibold text-gray-300 mb-2">Keine Kategorien ausgewählt</h2>
              <p className="text-sm text-gray-500">Aktiviere oben mindestens eine Kategorie</p>
            </div>
          ) : nothingToShow ? (
            /* Nichts im Umkreis */
            <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-10 text-center">
              <MapPinOff size={56} className="mx-auto text-zinc-600 mb-4" />
              <h2 className="text-lg font-semibold text-gray-300 mb-2">Keine Einträge in diesem Umkreis</h2>
              <p className="text-sm text-gray-500">
                Versuche einen größeren Umkreis oder andere Kategorien
              </p>
              {maxDistance < 500 && (
                <Button
                  size="sm"
                  variant="bordered"
                  className="mt-4 border-[#3fced1] text-[#3fced1]"
                  onPress={() => {
                    const next = DISTANCE_OPTIONS.find((d) => d > maxDistance);
                    if (next) setMaxDistance(next);
                  }}
                >
                  Umkreis erhöhen
                </Button>
              )}
            </div>
          ) : (
            /* Sektionen */
            <div className="space-y-8">
              {SECTIONS.map((section) => {
                const entries = filteredByType[section.key];
                if (!typeFilters[section.key] || entries.length === 0) return null;
                const isCollapsed = collapsedSections.has(section.key);
                const SectionIcon = section.icon;
                const SubtitleIcon = section.subtitleIcon;

                return (
                  <section key={section.key}>
                    {/* Sektions-Header (einklappbar) */}
                    <button
                      type="button"
                      onClick={() => toggleSection(section.key)}
                      className="w-full flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-zinc-900 transition-colors text-left"
                    >
                      <SectionIcon size={22} className="text-gray-400 shrink-0" />
                      <h2 className="text-xl font-bold text-white">{section.title}</h2>
                      <span className="px-2.5 py-0.5 rounded-full bg-zinc-800 text-xs font-semibold text-gray-300">
                        {entries.length}
                      </span>
                      <span className="ml-auto text-gray-500">
                        {isCollapsed ? <ChevronDown size={22} /> : <ChevronUp size={22} />}
                      </span>
                    </button>

                    {/* Einträge */}
                    {!isCollapsed && (
                      <div className="mt-2 space-y-4">
                        {entries.map((entry, idx) => {
                          const card = (
                            <div className="flex items-start gap-4 p-4 md:p-5 rounded-2xl border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 transition-colors">
                              <EntryImage
                                src={entry.image}
                                alt={entry.name || section.badgeLabel}
                                fallbackIcon={SectionIcon}
                              />
                              <div className="min-w-0 flex-1">
                                <h3 className="text-base md:text-lg font-bold text-white line-clamp-2">
                                  {entry.name || 'Unbenannt'}
                                </h3>
                                {entry.subtitle && entry.subtitle.trim() && (
                                  <div className="mt-1 flex items-start gap-1.5 text-sm text-gray-400">
                                    <SubtitleIcon size={15} className="mt-0.5 shrink-0" />
                                    <span className="line-clamp-2">{entry.subtitle}</span>
                                  </div>
                                )}
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-800 text-xs font-semibold text-gray-300">
                                    <Navigation size={12} />
                                    {formatDistance(entry.distance)}
                                  </span>
                                  <span className="px-2.5 py-1 rounded-full bg-[#3fced1]/10 text-xs font-semibold text-[#3fced1]">
                                    {section.badgeLabel}
                                  </span>
                                </div>
                              </div>
                              <ChevronRight size={22} className="text-zinc-600 shrink-0 mt-1" />
                            </div>
                          );

                          const key = `${section.key}-${entry.id || entry.slug || idx}`;
                          // Ohne Slug keine Detail-Seite verlinkbar → Karte ohne Link
                          if (!entry.slug) {
                            return <div key={key}>{card}</div>;
                          }
                          return (
                            <Link key={key} to={`${section.routePrefix}/${entry.slug}`} className="block">
                              {card}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NearbyPage;
